/** Sub-page 1: the fields this node type has, and what each one means. */

import { useState } from 'react'
import { EmptyState, Modal } from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { Field, FlagChip, SectionHeader } from '../../components'
import { ListInput } from '../../components'
import { usePropertyMutations } from '../../hooks'
import {
  ARRAY_SUBTYPES,
  DATA_TYPES,
  type ArraySubtype,
  type DataType,
  type NodeTypeDetail,
  type PropertyDef,
} from '../../types/brain.types'

export function PropertiesPanel({
  graphId,
  nodeTypeId,
  detail,
}: {
  graphId: string
  nodeTypeId: string
  detail: NodeTypeDetail
}) {
  const [editing, setEditing] = useState<PropertyDef | 'new' | null>(null)
  const { remove } = usePropertyMutations(graphId, nodeTypeId)
  const toast = useToast()

  const hasPrimaryKey = detail.properties.some((p) => p.is_primary_key)

  return (
    <div className="stack">
      <SectionHeader
        title="Properties"
        hint="The fields every record of this type can carry. Flags are not decoration: REQ is enforced by the pipeline, IDX is what matching blocks on, and PII is masked on read."
        action={
          <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
            Add property
          </button>
        }
      />

      {detail.properties.length > 0 && !hasPrimaryKey && (
        <div className="callout callout-warn">
          <p>
            No property is marked as the primary key. Records will still load, but nothing
            identifies them from the source system — mark whichever field the source uses as its
            id.
          </p>
        </div>
      )}

      {detail.properties.length === 0 ? (
        <EmptyState
          title="No properties yet"
          action={
            <button className="btn btn-primary" onClick={() => setEditing('new')}>
              Add the first property
            </button>
          }
        >
          Until this node type has properties, an extractor has nothing to fill in and its records
          would be empty.
        </EmptyState>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Property</th>
                <th>Type</th>
                <th>Flags</th>
                <th>Description</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {detail.properties.map((property) => (
                <tr key={property.property_id}>
                  <td className="mono">
                    <strong>{property.key}</strong>
                    {property.enum_values.length > 0 && (
                      <div className="small faint">{property.enum_values.join(' | ')}</div>
                    )}
                  </td>
                  <td>
                    <span className="badge badge-neutral">
                      {property.data_type === 'array'
                        ? `array<${property.subtype ?? 'string'}>`
                        : property.data_type}
                    </span>
                  </td>
                  <td>
                    <span className="row-tight">
                      {property.flags.length === 0 && <span className="faint">—</span>}
                      {property.flags.map((flag) => (
                        <FlagChip key={flag} flag={flag} />
                      ))}
                    </span>
                  </td>
                  <td className="muted small">{property.description || '—'}</td>
                  <td>
                    <div className="row-tight">
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditing(property)}>
                        Edit
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={async () => {
                          if (
                            !window.confirm(
                              `Delete '${property.key}'? Rules that reference it stop applying.`,
                            )
                          )
                            return
                          try {
                            await remove.mutateAsync(property.property_id)
                            toast.success(`Deleted ${property.key}.`)
                          } catch (error) {
                            toast.error(error)
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <PropertyDialog
          graphId={graphId}
          nodeTypeId={nodeTypeId}
          existing={editing === 'new' ? undefined : editing}
          order={detail.properties.length}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

function PropertyDialog({
  graphId,
  nodeTypeId,
  existing,
  order,
  onClose,
}: {
  graphId: string
  nodeTypeId: string
  existing?: PropertyDef
  order: number
  onClose: () => void
}) {
  const { create, update } = usePropertyMutations(graphId, nodeTypeId)
  const toast = useToast()

  const [key, setKey] = useState(existing?.key ?? '')
  const [dataType, setDataType] = useState<DataType>(existing?.data_type ?? 'string')
  const [subtype, setSubtype] = useState<ArraySubtype>(
    (existing?.subtype as ArraySubtype) ?? 'string',
  )
  const [description, setDescription] = useState(existing?.description ?? '')
  const [flags, setFlags] = useState({
    is_primary_key: existing?.is_primary_key ?? false,
    is_required: existing?.is_required ?? false,
    is_indexed: existing?.is_indexed ?? false,
    is_pii: existing?.is_pii ?? false,
  })
  const [enumValues, setEnumValues] = useState<string[]>(existing?.enum_values ?? [])

  const busy = create.isPending || update.isPending

  async function save() {
    const body = {
      key: key.trim(),
      data_type: dataType,
      // Only meaningful for an array; the API drops it for anything else.
      subtype: dataType === 'array' ? subtype : null,
      description: description.trim() || null,
      enum_values: dataType === 'enum' ? enumValues : [],
      ...flags,
    }
    try {
      if (existing) {
        await update.mutateAsync({ id: existing.property_id, body })
      } else {
        await create.mutateAsync({ ...body, order_index: order })
      }
      toast.success(`Saved ${body.key}.`)
      onClose()
    } catch (error) {
      toast.error(error)
    }
  }

  return (
    <Modal
      title={existing ? `Edit ${existing.key}` : 'New property'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={save}
            disabled={busy || !key.trim() || (dataType === 'enum' && enumValues.length === 0)}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="field-pair">
          <Field label="Key" required help="Stored as snake_case.">
            <input
              className="input"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="account_name"
              disabled={Boolean(existing)}
            />
          </Field>
          <Field label="Type" help={TYPE_HELP[dataType]}>
            {/* Two controls when the type is a container, one otherwise. An
                array of strings and an array of integers are the same decision
                made twice, not two different types to scroll past. */}
            <div className="row-tight">
              <select
                className="select"
                style={{ flex: 1 }}
                value={dataType}
                onChange={(e) => setDataType(e.target.value as DataType)}
              >
                {DATA_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              {dataType === 'array' && (
                <select
                  className="select"
                  style={{ flex: 1 }}
                  aria-label="What the array holds"
                  value={subtype}
                  onChange={(e) => setSubtype(e.target.value as ArraySubtype)}
                >
                  {ARRAY_SUBTYPES.map((t) => (
                    <option key={t} value={t}>
                      of {t}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </Field>
        </div>

        {dataType === 'enum' && (
          <Field label="Allowed values" required help="Comma separated.">
            <ListInput value={enumValues} onChange={setEnumValues} placeholder="open, won, lost" />
          </Field>
        )}

        <Field label="What is it?">
          <input
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="The company's trading name."
          />
        </Field>

        <div className="stack" style={{ gap: 8 }}>
          <FlagToggle
            label="Primary key"
            hint="The source system's own id for this record."
            checked={flags.is_primary_key}
            onChange={(v) => setFlags((f) => ({ ...f, is_primary_key: v }))}
          />
          <FlagToggle
            label="Required"
            hint="A record without it fails quality checks and is held back rather than made golden."
            checked={flags.is_required}
            onChange={(v) => setFlags((f) => ({ ...f, is_required: v }))}
          />
          <FlagToggle
            label="Indexed"
            hint="Worth blocking on when matching — a field that narrows candidates quickly."
            checked={flags.is_indexed}
            onChange={(v) => setFlags((f) => ({ ...f, is_indexed: v }))}
          />
          <FlagToggle
            label="Personal data"
            hint="Masked whenever a record is read, unless the caller is allowed the raw value."
            checked={flags.is_pii}
            onChange={(v) => setFlags((f) => ({ ...f, is_pii: v }))}
          />
        </div>
      </div>
    </Modal>
  )
}

const TYPE_HELP: Record<DataType, string> = {
  string: 'Short text.',
  text: 'Long text.',
  number: 'A number. "$1.2M" is parsed to 1200000 on the way in.',
  integer: 'A whole number.',
  boolean: 'True or false.',
  date: 'A calendar date.',
  datetime: 'A moment in time.',
  enum: 'One of a fixed list you define below.',
  email: 'Lowercased on the way in, and comparable by domain.',
  phone: 'Normalised toward E.164, and compared on the last ten digits.',
  url: 'A web address.',
  json: 'Structured data kept as-is.',
  array:
    'A list of values, all of the same kind. Rules ask about its members and its length rather than its text.',
}

function FlagToggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string
  hint: string
  checked: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <label className="checkbox-row">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        <strong style={{ fontWeight: 570 }}>{label}</strong>
        <div className="small muted">{hint}</div>
      </span>
    </label>
  )
}
